/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import {
  Alert,
  Grid,
  Group,
  Input,
  NumberInput,
  Paper,
  SimpleGrid,
  Space,
  Text,
  Textarea,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertTriangle,
  IconCurrencyReal,
  IconNotes,
  IconScale,
  IconZoomMoney,
} from '@tabler/icons-react';
import { Tabs } from 'antd';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import BasicPricing, {
  BasicPricingData,
  BasicPricingRef,
} from '../../../../../../../components/BasicPricing';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../../../../components/BasicValueUnits';
import EntityItemPicker from '../../../../../../../components/core/EntityItemPicker/EntityItemPicker';
import PageSection from '../../../../../../../components/core/PageSection/PageSection';
import ToleranceForm from '../../../../../../../components/core/Tolerance/ToleranceForm';
import { useEventGeneralContext } from '../../../../../../../contexts/core/events/EventGeneral.context';
import { useEventServiceContext } from '../../../../../../../contexts/core/events/EventService.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../../../../models/core/cache.type';
import { Action } from '../../../../../../../models/core/core.type';
import { EntitySearchResponseType } from '../../../../../../../models/core/entities.type';
import { EventServiceQuotationType } from '../../../../../../../models/core/events.type';
import {
  ProposalResiduePlanPricingType,
  ProposalTipoPrecificacaoEnum,
} from '../../../../../../../models/core/proposals.type';
import { EntityItemEnum, Feature, Financial } from '../../../../../../../utils/constants.utils';
import { buildFakeAuditObject, isNullOrEmpty, newGuid } from '../../../../../../../utils/helper.utils';

type FormViewData = {
  action: Action;
  id: string;
  fornecedorData: EntitySearchResponseType | null;
  idFornecedor: number | null;
  fornecedor: string;
  quantidade: number | null;
  frequencia: number | null;
  frequenciaIdUnidadeMedida1: string | null;
  frequenciaIdUnidadeMedida2: string | null;
  preco: number | null;
  precificacao: BasicPricingData;
  observacao: string | null;
  frequenciaFornecedor: unknown;
};

type FormViewProps = {
  referenceData: {
    entityTypeData: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  origItem: EventServiceQuotationType | null;
  item: EventServiceQuotationType | null;
  disabled: boolean;
  zIndex: number;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (isNullOrEmpty(value)) {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const EventServiceQuotationFormAddEdit = forwardRef((props: FormViewProps, ref) => {
  const { eventGeneralData } = useEventGeneralContext();
  const cotacaoProposta = eventGeneralData?.propostaServicoData?.cotacoes?.find((y) => y.primario);
  const { eventServiceData } = useEventServiceContext();
  const refToleranceForm = useRef<any>();
  const refCustomerPricing = useRef<BasicPricingRef>(null);
  const refSupplierFrequency = useRef<BasicValueUnitsRef>(null);

  const buildFornecedorDataFromItem = (
    item: EventServiceQuotationType | null
  ): EntitySearchResponseType | null => {
    if (!item) {
      return null;
    }

    return {
      ...buildFakeAuditObject(),
      idEntidade: Number(item.idFornecedor),
      tipos: [],
      cnpj: item.fornecedorCNPJ || null,
      razaoSocial: item.fornecedorRazaoSocial || null,
      nomeFantasia: item.fornecedorNomeFantasia || null,
      incricaoEstadual: null,
      incricaoMunicipal: null,
      cpf: item.fornecedorCPF || null,
      nome: item.fornecedorNome || null,
      webSite: null,
      aceitaEncontroContas: false,
    };
  };

  const quantityUnitOptions = props.referenceData.unitOfMeasures
    .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const frequencyUnit2Options = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const buildInitialCustomerPricing = (item: EventServiceQuotationType | null): BasicPricingData => ({
    tipoPrecificacao: ProposalTipoPrecificacaoEnum.Especifica,
    quantidade:
      item?.precificacao?.quantidade ??
      cotacaoProposta?.precificacao?.quantidade ??
      eventServiceData.quantidade ??
      1,
    quantidadeIdUnidadeMedida:
      item?.precificacao?.quantidadeIdUnidadeMedida?.toString() ||
      cotacaoProposta?.precificacao?.quantidadeIdUnidadeMedida?.toString() ||
      props.referenceData.unitOfMeasures
        .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        ?.idUnidadeMedida.toString() ||
      null,
    frequenciaIdUnidadeMedida:
      item?.precificacao?.frequenciaIdUnidadeMedida?.toString() ||
      cotacaoProposta?.precificacao?.frequenciaIdUnidadeMedida?.toString() ||
      eventServiceData.frequenciaIdUnidadeMedida2?.toString() ||
      null,
    preco: item?.precificacao?.preco ?? cotacaoProposta?.precificacao?.preco ?? 0,
    imposto: item?.precificacao?.imposto ?? cotacaoProposta?.precificacao?.imposto ?? Financial.Tax,
  });

  const buildPricingTypeFromQuotation = (pricingData: BasicPricingData): ProposalResiduePlanPricingType => {
    const quantidadeIdUnidadeMedida = pricingData.quantidadeIdUnidadeMedida
      ? Number(pricingData.quantidadeIdUnidadeMedida)
      : null;
    const frequenciaIdUnidadeMedida = pricingData.frequenciaIdUnidadeMedida
      ? Number(pricingData.frequenciaIdUnidadeMedida)
      : null;

    return {
      tipo: ProposalTipoPrecificacaoEnum.Especifica,
      quantidade: pricingData.quantidade,
      quantidadeIdUnidadeMedida,
      quantidadeUnidadeMedida:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedida || null,
      quantidadeUnidadeMedidaSigla:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedidaSigla || null,
      frequenciaIdUnidadeMedida,
      frequenciaUnidadeMedida:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)
          ?.unidadeMedida || null,
      frequenciaUnidadeMedidaSigla:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)
          ?.unidadeMedidaSigla || null,
      preco: pricingData.preco,
      imposto: pricingData.imposto,
    };
  };

  const form = useForm<FormViewData>({
    initialValues: {
      action: props.origItem ? Action.Nothing : Action.Add,
      id: props.item?.id || newGuid(),
      fornecedorData: buildFornecedorDataFromItem(props.item),
      idFornecedor: props.item?.idFornecedor || null,
      fornecedor:
        props.item?.fornecedorNomeFantasia ||
        props.item?.fornecedorRazaoSocial ||
        props.item?.fornecedorNome ||
        '',
      quantidade: props.item?.quantidade ?? null,
      frequencia: props.item?.frequencia ?? null,
      frequenciaIdUnidadeMedida1:
        props.item?.frequenciaIdUnidadeMedida1?.toString() ||
        eventServiceData.frequenciaIdUnidadeMedida1?.toString() ||
        props.referenceData.unitOfMeasures
          .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
          ?.idUnidadeMedida.toString() ||
        null,
      frequenciaIdUnidadeMedida2:
        props.item?.frequenciaIdUnidadeMedida2?.toString() ||
        eventServiceData.frequenciaIdUnidadeMedida2?.toString() ||
        null,
      preco: props.item?.preco === 0 ? 0 : props.item?.preco || null,
      precificacao: buildInitialCustomerPricing(props.item),
      observacao: props.item?.observacao || '',
      frequenciaFornecedor: null,
    },
    validate: {
      idFornecedor: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
    },
  });

  const compativel =
    cotacaoProposta?.quantidade === form.values.quantidade &&
    cotacaoProposta?.frequenciaIdUnidadeMedida1 === Number(form.values.frequenciaIdUnidadeMedida1) &&
    cotacaoProposta?.frequenciaIdUnidadeMedida2 === Number(form.values.frequenciaIdUnidadeMedida2);

  const isModified = (
    frequencyData?: BasicValueUnitsData | null,
    customerPricingData?: BasicPricingData | null
  ): boolean => {
    const normalize = (value: unknown) => (isNullOrEmpty(value) ? '' : String(value));

    const origData = `${normalize(props.origItem?.idFornecedor)}|${normalize(
      props.origItem?.quantidade
    )}|${normalize(null)}|${normalize(props.origItem?.frequenciaIdUnidadeMedida1)}|${normalize(
      props.origItem?.frequenciaIdUnidadeMedida2
    )}|${normalize(props.origItem?.preco)}|${normalize(props.origItem?.precificacao?.quantidade)}|${normalize(
      props.origItem?.precificacao?.quantidadeIdUnidadeMedida
    )}|${normalize(props.origItem?.precificacao?.frequenciaIdUnidadeMedida)}|${normalize(
      props.origItem?.precificacao?.preco
    )}|${normalize(props.origItem?.precificacao?.imposto)}|${normalize(props.origItem?.observacao)}`;

    const formData = `${normalize(form.values.idFornecedor)}|${normalize(frequencyData?.valor)}|${normalize(
      null
    )}|${normalize(frequencyData?.idUnidade1)}|${normalize(frequencyData?.idUnidade2)}|${normalize(
      form.values.preco
    )}|${normalize(customerPricingData?.quantidade)}|${normalize(
      customerPricingData?.quantidadeIdUnidadeMedida
    )}|${normalize(customerPricingData?.frequenciaIdUnidadeMedida)}|${normalize(
      customerPricingData?.preco
    )}|${normalize(customerPricingData?.imposto)}|${normalize(form.values.observacao)}`;

    return origData !== formData;
  };

  const clearFornecedorData = () => {
    form.setFieldValue('preco', null);
    form.setFieldValue('quantidade', null);
    form.setFieldValue('frequencia', null);
    form.setFieldValue('frequenciaIdUnidadeMedida1', null);
    form.setFieldValue('frequenciaIdUnidadeMedida2', null);
    refSupplierFrequency.current?.clear();
  };

  useImperativeHandle(ref, () => ({
    validate(): EventServiceQuotationType | null {
      const validationResult = form.validate();
      const frequencyData = refSupplierFrequency.current?.validate();

      if (!refCustomerPricing.current || !frequencyData) {
        return null;
      }

      const customerPricing = refCustomerPricing.current.validate();
      frequencyData.valor = isNullOrEmpty(frequencyData.valor) ? null : Number(frequencyData.valor);

      const hasBlockingErrors = Object.entries(validationResult.errors).some(([field, message]) => {
        return ['idFornecedor'].includes(field) && !!message;
      });

      if (hasBlockingErrors || customerPricing === null) {
        return null;
      }

      if (!isNullOrEmpty(form.values.preco) && isNullOrEmpty(frequencyData.valor)) {
        form.setFieldError(
          'frequenciaFornecedor',
          <Text size="xs" mt={5}>
            Campo obrigatório quando o preço é informado.
          </Text>
        );
        return null;
      }

      let { values } = form;
      if (props.origItem) {
        if (isModified(frequencyData, customerPricing)) {
          values.action = Action.Modify;
        } else {
          values = {
            ...values,
            ...props.origItem,
            action: Action.Nothing,
            precificacao: form.values.precificacao,
            quantidade: frequencyData.valor,
            frequencia: null,
            frequenciaIdUnidadeMedida1: frequencyData.idUnidade1 || '',
            frequenciaIdUnidadeMedida2: frequencyData.idUnidade2 || '',
          };
        }
      }

      const formItem: EventServiceQuotationType = {
        ...values,
        ...buildFakeAuditObject(),
        idTemporaria: values.id,
        idEventoServico: props.item?.idEventoServico || -1,
        idEventoServicoCotacao: props.item?.idEventoServicoCotacao || -1,
        precificacao: buildPricingTypeFromQuotation(customerPricing),
      };

      formItem.quantidade = frequencyData.valor;
      formItem.frequencia = null;
      formItem.frequenciaIdUnidadeMedida1 = toNumberOrNull(frequencyData.idUnidade1);
      formItem.frequenciaUnidadeMedida1 =
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === Number(frequencyData.idUnidade1))
          ?.unidadeMedida || '';

      formItem.frequenciaIdUnidadeMedida2 = toNumberOrNull(frequencyData.idUnidade2);
      formItem.frequenciaUnidadeMedida2 =
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === Number(frequencyData.idUnidade2))
          ?.unidadeMedida || '';

      formItem.preco = toNumberOrNull(formItem.preco);
      formItem.idFornecedor = toNumberOrNull(form.values.fornecedorData?.idEntidade);
      formItem.fornecedorCNPJ = form.values.fornecedorData?.cnpj || null;
      formItem.fornecedorRazaoSocial = form.values.fornecedorData?.razaoSocial || null;
      formItem.fornecedorNomeFantasia = form.values.fornecedorData?.nomeFantasia || null;
      formItem.fornecedorCPF = form.values.fornecedorData?.cpf || null;
      formItem.fornecedorNome = form.values.fornecedorData?.nome || null;
      formItem.observacao = formItem.observacao?.trim() || null;
      formItem.tolerancias = refToleranceForm?.current?.validate() || [];

      delete (formItem as any).fornecedorData;

      if (!isNullOrEmpty(cotacaoProposta?.precificacao?.preco)) {
        formItem.precoReferencia = cotacaoProposta?.precificacao?.preco ?? null;
        formItem.precoReferenciaCompativel =
          cotacaoProposta?.precificacao?.quantidade === formItem.precificacao?.quantidade &&
          cotacaoProposta?.precificacao?.quantidadeIdUnidadeMedida ===
            formItem.precificacao?.quantidadeIdUnidadeMedida &&
          cotacaoProposta?.precificacao?.frequenciaIdUnidadeMedida ===
            formItem.precificacao?.frequenciaIdUnidadeMedida;
      } else {
        formItem.precoReferencia = null;
        formItem.precoReferenciaCompativel = false;
      }

      return formItem;
    },
  }));

  const tabs = [
    {
      key: 'Cotacao',
      label: (
        <Group>
          <ThemeIcon color={Feature.Home.Event.color} variant="outline">
            <IconZoomMoney size={18} />
          </ThemeIcon>
          <Text size="md" color={Feature.Home.Event.color} style={{ fontWeight: 500 }}>
            Cotação
          </Text>
        </Group>
      ),
      children: (
        <>
          {cotacaoProposta && !compativel && (
            <>
              <Alert icon={<IconAlertTriangle size={16} />} title="Atenção!" color="yellow">
                A quantidade/frequência cotada abaixo não é compatível com a do plano.
              </Alert>
              <Space h="md" />
            </>
          )}

          <Paper shadow="xs" p="md" withBorder>
            <PageSection
              size="md"
              color={Feature.Home.Event.color}
              label="Cliente"
              text="Informações operacionais da cotação com o cliente."
            />
            <Space h="xs" />
            <BasicPricing
              ref={refCustomerPricing}
              isEdit
              disabled={props.disabled}
              value={form.values.precificacao}
              quantityUnitOptions={quantityUnitOptions}
              frequencyUnitOptions={frequencyUnit2Options}
              withFrequency
              hideTipoPrecificacaoSelector
              forcePricingActive
              quantityUnitDisabled
              zIndex={props.zIndex + 1}
              onChange={(value) => form.setFieldValue('precificacao', value)}
            />
          </Paper>
          <Space h="md" />

          <Paper shadow="xs" p="md" withBorder>
            <Group justify="space-between">
              <PageSection
                size="md"
                color={Feature.Home.Event.color}
                label="Fornecedor"
                text="Informações operacionais da cotação com o fornecedor."
              />
              {!!form.values.idFornecedor && (
                <Text
                  component="button"
                  type="button"
                  size="sm"
                  c="orange"
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: props.disabled ? 'default' : 'pointer',
                  }}
                  onClick={() => {
                    if (!props.disabled) {
                      clearFornecedorData();
                    }
                  }}
                >
                  Limpar
                </Text>
              )}
            </Group>
            <Space h="xs" />

            <Grid columns={4}>
              <Grid.Col span={4}>
                <EntityItemPicker
                  key={
                    (eventServiceData.tipo === EntityItemEnum.Packaging
                      ? eventServiceData.idResiduoAcondicionamento
                      : eventServiceData.tipo === EntityItemEnum.Equipment
                      ? eventServiceData.idResiduoEquipamento
                      : eventServiceData.idServico) || newGuid()
                  }
                  label="Fornecedor"
                  item={eventServiceData.tipo}
                  itemId={
                    (eventServiceData.tipo === EntityItemEnum.Packaging
                      ? eventServiceData.idResiduoAcondicionamento
                      : eventServiceData.tipo === EntityItemEnum.Equipment
                      ? eventServiceData.idResiduoEquipamento
                      : eventServiceData.idServico) || -1
                  }
                  formItem={form.getInputProps('idFornecedor') || undefined}
                  idsToBeDisabled={[]}
                  callback={async (item: EntitySearchResponseType | null) => {
                    if (item) {
                      form.setFieldValue('fornecedorData', item);
                      form.setFieldValue('idFornecedor', item.idEntidade);
                      form.setFieldValue(
                        'fornecedor',
                        item.nomeFantasia || item.razaoSocial || item.nome || ''
                      );
                    }
                  }}
                  required
                  disabled={props.disabled}
                  zIndex={props.zIndex + 1}
                />
              </Grid.Col>
            </Grid>
            <Space h="xs" />

            <Grid columns={4}>
              <Grid.Col span={3}>
                <Input.Wrapper {...form.getInputProps('frequenciaFornecedor')}>
                  <BasicValueUnits
                    ref={refSupplierFrequency}
                    isEdit
                    disabled={props.disabled}
                    disableUnit1
                    zIndex={props.zIndex + 1}
                    label="Quantidade/Frequência"
                    showValue
                    value={{
                      valor: form.values.quantidade,
                      idUnidade1: form.values.frequenciaIdUnidadeMedida1,
                      idUnidade2: form.values.frequenciaIdUnidadeMedida2,
                    }}
                    onChange={(frequencyValue) => {
                      form.setFieldValue('quantidade', toNumberOrNull(frequencyValue.valor));
                      form.setFieldValue('frequencia', null);
                      form.setFieldValue('frequenciaIdUnidadeMedida1', frequencyValue.idUnidade1);
                      form.setFieldValue('frequenciaIdUnidadeMedida2', frequencyValue.idUnidade2);
                    }}
                    unit1Options={quantityUnitOptions}
                    unit2Options={frequencyUnit2Options}
                    validationMode="all-or-none"
                    valueMin={eventServiceData.tipo === EntityItemEnum.Service ? 0.5 : 1}
                    valueDefaultValue={1}
                    valueStep={eventServiceData.tipo === EntityItemEnum.Service ? 0.5 : 1}
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={1}>
                <NumberInput
                  leftSection={<IconCurrencyReal size={15} />}
                  label="Preço"
                  placeholder="Preço"
                  min={0}
                  decimalScale={2}
                  hideControls
                  {...form.getInputProps('preco')}
                  disabled={props.disabled}
                />
              </Grid.Col>
            </Grid>
            <Space h="xs" />

            <SimpleGrid cols={1}>
              <Textarea
                leftSection={<IconNotes size={15} />}
                label="Observação"
                placeholder="Observação sobre a cotação do serviço"
                autosize
                maxLength={500}
                {...form.getInputProps('observacao')}
                disabled={props.disabled}
              />
            </SimpleGrid>
          </Paper>
        </>
      ),
      forceRender: true,
    },
    {
      key: 'Tolerancias',
      label: (
        <Group>
          <ThemeIcon color={Feature.Home.Event.color} variant="outline">
            <IconScale size={18} />
          </ThemeIcon>
          <Text size="md" color={Feature.Home.Event.color} style={{ fontWeight: 500 }}>
            Tolerâncias
          </Text>
        </Group>
      ),
      children: (
        <Paper shadow="xs" p="md" withBorder>
          <ToleranceForm
            ref={refToleranceForm}
            refId={props.item?.idEventoServicoCotacao}
            title="Tolerâncias"
            description="Tolerâncias para esta cotação."
            color={Feature.Home.Event.color}
            disabled={props.disabled}
            origData={props.origItem?.tolerancias || []}
            data={props.item?.tolerancias || []}
            referenceData={props.referenceData}
            zIndex={props.zIndex + 1}
          />
        </Paper>
      ),
      forceRender: true,
    },
  ];

  return (
    <form id="event-service-quotation" noValidate>
      <Tabs items={tabs} defaultActiveKey="Cotacao" />
    </form>
  );
});

export default EventServiceQuotationFormAddEdit;
